import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../components/common/PageHeader";
import {
    getClientById,
    getClientContacts,
    updateClient,
    updateContactPerson,
    createContactPerson,
    deleteContactPerson,
} from "../api/clientService";
import { toast } from "react-toastify";
import "../styles/client.css";

/* ================= TYPES ================= */

interface Contact {
    id?: number;
    name: string;
    phone: string;
    email: string;
    designation: string;
    is_master?: boolean;
}

/* ================= COMPONENT ================= */

const EditClient: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deletedContacts, setDeletedContacts] = useState<number[]>([]);

    const [formData, setFormData] = useState({
        clientName: "",
        pan: "",
        gstin: "",
        billingAddress: {
            address1: "",
            address2: "",
            city: "",
            state: "",
            pincode: "",
        },
        contacts: [] as Contact[],
    });

    /* ================= LOAD DATA ================= */

    useEffect(() => {
        if (id) loadClient(Number(id));
    }, [id]);

    const loadClient = async (clientId: number) => {
        try {
            setLoading(true);

            const clientRes = await getClientById(clientId);
            const contactRes = await getClientContacts(clientId);

            setFormData({
                clientName: clientRes.data.name,
                pan: clientRes.data.pan_no || "",
                gstin: clientRes.data.gst_no || "",
                billingAddress: {
                    address1: clientRes.data.address_line_1 || "",
                    address2: clientRes.data.address_line_2 || "",
                    city: clientRes.data.city || "",
                    state: clientRes.data.state || "",
                    pincode: clientRes.data.pincode || "",
                },
                contacts: contactRes.data.results.map((c: any) => ({
    id: c.id,
    name: c.name,
    phone: c.mobile_no,
    email: c.email,
    designation: c.designation,
    is_master: c.is_master,
})),

            });
        } catch {
            toast.error("Failed to load client");
        } finally {
            setLoading(false);
        }
    };

    /* ================= VALIDATION ================= */

    const validateForm = () => {
        const e: Record<string, string> = {};

        if (!formData.clientName.trim()) e.clientName = "Client name is required";

        if (
            formData.pan &&
            !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan)
        ) {
            e.pan = "Invalid PAN format";
        }

        if (
            formData.gstin &&
            !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
                formData.gstin
            )
        ) {
            e.gstin = "Invalid GST format";
        }

        if (
            formData.billingAddress.pincode &&
            !/^[1-9][0-9]{5}$/.test(formData.billingAddress.pincode)
        ) {
            e.pincode = "Invalid pincode";
        }

        formData.contacts.forEach((c, i) => {
            if (!c.name) e[`name_${i}`] = "Required";
            if (!/^[6-9]\d{9}$/.test(c.phone))
                e[`phone_${i}`] = "Invalid mobile";
            if (
    c.email &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)
) {
    e[`email_${i}`] = "Invalid email";
}
            if (!c.designation)
                e[`designation_${i}`] = "Required";
        });

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    /* ================= HANDLERS ================= */

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddressChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            billingAddress: {
                ...prev.billingAddress,
                [name]: value,
            },
        }));
    };

    const handleContactChange = <
        K extends keyof Contact
    >(
        index: number,
        field: K,
        value: Contact[K]
    ) => {
        const updated = [...formData.contacts];
        updated[index] = {
            ...updated[index],
            [field]: value,
        };
        setFormData(prev => ({ ...prev, contacts: updated }));
    };

    const addContact = () => {
        setFormData(prev => ({
            ...prev,
            contacts: [
                ...prev.contacts,
                { name: "", phone: "", email: "", designation: "" },
            ],
        }));
    };

    const removeContact = (index: number) => {
        const contact = formData.contacts[index];
        const contactId = contact.id;

        // ✅ mark for soft delete only if it exists in DB
        if (typeof contactId === "number") {
            setDeletedContacts(prev => [...prev, contactId]);
        }

        // remove from UI
        setFormData(prev => ({
            ...prev,
            contacts: prev.contacts.filter((_, i) => i !== index),
        }));
    };



    /* ================= SUBMIT ================= */

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error("Fix validation errors");
            return;
        }

        try {
            setLoading(true);

            await updateClient(Number(id), {
                name: formData.clientName,
                pan_no: formData.pan,
                gst_no: formData.gstin,
                address_line_1: formData.billingAddress.address1,
                address_line_2: formData.billingAddress.address2,
                city: formData.billingAddress.city,
                state: formData.billingAddress.state,
                pincode: formData.billingAddress.pincode,
            });

            // 1️⃣ Soft delete removed contacts
            for (const contactId of deletedContacts) {
                await deleteContactPerson(contactId);
            }

            // 2️⃣ Update / create active contacts
            for (const c of formData.contacts) {
                if (c.id) {
                    await updateContactPerson(c.id, {
                        name: c.name,
                        mobile_no: c.phone,
                        email: c.email,
                        designation: c.designation,
                           is_master: c.is_master ?? false,

                    });
                } else {
                    await createContactPerson({
                        customer_id: Number(id),
                        name: c.name,
                        mobile_no: c.phone,
                        email: c.email,
                        designation: c.designation,
                        is_master: c.is_master ?? false,
                    });
                }
            }




            toast.success("Client updated successfully");
            navigate("/masters/client");
        } catch {
            toast.error("Failed to update client");
        } finally {
            setLoading(false);
        }
    };

    /* ================= UI ================= */

    return (
        <>
            <PageHeader
                title="Edit Client"
                breadcrumbs={[
                    { label: "Dashboard", path: "/dashboard" },
                    { label: "Masters" },
                    { label: "Client Master", path: "/masters/client" },
                    { label: "Edit Client" },
                ]}
            />

            <div className="container mt-4">
                <div className="card common-form-card p-4">

                    <Section title="Client Details">
                        <Input label="Client Name *" name="clientName" value={formData.clientName} onChange={handleChange} error={errors.clientName} />

                        <Input label="PAN" name="pan" value={formData.pan} onChange={handleChange} error={errors.pan} />
                        <Input label="GST" name="gstin" value={formData.gstin} onChange={handleChange} error={errors.gstin} />
                    </Section>

                    <Section title="Contact Details" headerAction={
                        <button className="btn btn-light btn-sm" onClick={addContact}>
                            Add Contact
                        </button>
                    }>
                        {formData.contacts.length === 0 && (
                            <div className="col-12 text-center text-muted mt-0">
                                No Contact Available
                            </div>
                        )}
                        {formData.contacts.map((c, i) => (
                            <React.Fragment key={i}>
                                <Input
                                    label="Name *"
                                    value={c.name}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        handleContactChange(i, "name", e.target.value)
                                    }
                                    error={errors[`name_${i}`]}
                                />

                                <Input
                                    label="Mobile *"
                                    value={c.phone}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        handleContactChange(i, "phone", e.target.value)
                                    }
                                    error={errors[`phone_${i}`]}
                                />

                                <Input
                                    label="Email"
                                    value={c.email}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        handleContactChange(i, "email", e.target.value)
                                    }
                                    error={errors[`email_${i}`]}
                                />

                                <div className="col-md-2">
                                    <div className="form-floating">
                                        <input
                                            className={`form-control ${errors[`designation_${i}`] ? "is-invalid" : ""}`}
                                            placeholder="Designation *"
                                            value={c.designation}
                                            onChange={(e) =>
                                                handleContactChange(i, "designation", e.target.value)
                                            }
                                        />
                                        <label>Designation *</label>
                                        {errors[`designation_${i}`] && (
                                            <div className="invalid-feedback">
                                                {errors[`designation_${i}`]}
                                            </div>
                                        )}
                                    </div>
                                </div>


                                <div className="col-md-1 d-flex align-items-center justify-content-center">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => removeContact(i)}
                                    >
                                        ✕
                                    </button>
                                </div>

                            </React.Fragment>
                        ))}


                    </Section>
                    <Section title="Billing Address">
                        <Input label="Address Line 1" name="address1" value={formData.billingAddress.address1} onChange={handleAddressChange} />
                        <Input label="Address Line 2" name="address2" value={formData.billingAddress.address2} onChange={handleAddressChange} />
                        <Input label="City" name="city" value={formData.billingAddress.city} onChange={handleAddressChange} />
                        <Input label="State" name="state" value={formData.billingAddress.state} onChange={handleAddressChange} />
                        <Input label="Pincode" name="pincode" value={formData.billingAddress.pincode} onChange={handleAddressChange} error={errors.pincode} />
                    </Section>

                    <div className="d-flex justify-content-end gap-2 mt-4">
                       
                        <button className="btn btn-outline-secondary" onClick={() => navigate("/masters/client")}>
                            Cancel
                        </button>
                         <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                            {loading ? "Saving..." : "Update"}
                        </button>
                    </div>

                </div>
            </div>
        </>
    );
};

export default EditClient;

/* ================= HELPERS ================= */

const Section = ({ title, children, headerAction }: any) => (
    <div className="form-section mt-4">
        <div className="form-section-header bg-primary text-white d-flex justify-content-between align-items-center">
            <span>{title}</span>
            {headerAction}
        </div>
        <div className="row g-3 p-3">{children}</div>
    </div>
);


const Input = ({ label, error, ...props }: any) => (
    <div className="col-md-3">
        <div className="form-floating">
            <input
                className={`form-control ${error ? "is-invalid" : ""}`}
                placeholder={label}
                {...props}
            />
            <label>{label}</label>
            {error && <div className="invalid-feedback">{error}</div>}
        </div>
    </div>
);

